import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError, fmtINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import Payslip from "@/components/Payslip";
import OfferLetter from "@/components/OfferLetter";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { downloadLetterPdf } from "@/lib/pdf";
import { LoginActivity } from "@/components/LoginActivity";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader2, Save, Palmtree, Clock, Wallet, Eye, Download, ShieldCheck, Building2, Landmark, Phone,
  Upload, FileText, Trash2, FolderLock, History, Calculator, KeyRound, Cake,
} from "lucide-react";
import { toast } from "sonner";

const BAL_TINTS = {
  Casual: "bg-blue-50 text-blue-600 dark:bg-blue-950/50 dark:text-blue-400",
  Sick: "bg-rose-50 text-rose-600 dark:bg-rose-950/50 dark:text-rose-400",
  Earned: "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400",
};

export default function MySpace() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [viewSlip, setViewSlip] = useState(null);
  const [viewOffer, setViewOffer] = useState(false);
  const [tax, setTax] = useState(null);
  const [form, setForm] = useState(null);
  const [expiry, setExpiry] = useState({});

  const { data: emp, isLoading } = useQuery({
    queryKey: ["me-employee"],
    queryFn: async () => (await api.get("/employees/me")).data,
  });
  const { data: balance = {} } = useQuery({
    queryKey: ["leave-balance"], queryFn: async () => (await api.get("/leaves/balance")).data,
  });
  const { data: payslips = [] } = useQuery({
    queryKey: ["payroll"], queryFn: async () => (await api.get("/payroll")).data,
  });
  const { data: attendance = [] } = useQuery({
    queryKey: ["attendance-logs"], queryFn: async () => (await api.get("/attendance")).data,
  });
  const { data: myRequests = [] } = useQuery({
    queryKey: ["my-profile-requests"], queryFn: async () => (await api.get("/profile/requests")).data,
  });

  const { data: myOffer } = useQuery({
    queryKey: ["my-offer"], queryFn: async () => (await api.get("/offers/mine")).data, retry: false,
  });
  const taxMut = useMutation({
    mutationFn: async (body) => (await api.put("/tax/declaration", body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["me-employee"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); setTax(null); toast.success("Tax regime saved — applies from the next payroll run"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const reqMut = useMutation({
    mutationFn: async (body) => (await api.post("/profile/request", body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["my-profile-requests"] }); setForm(null); toast.success("Update request sent to HR for approval"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const { data: documents = [] } = useQuery({
    queryKey: ["my-documents"], queryFn: async () => (await api.get("/documents")).data,
  });
  const { data: auditLog = [] } = useQuery({
    queryKey: ["my-profile-audit"], queryFn: async () => (await api.get("/profile/audit")).data,
  });
  const uploadMut = useMutation({
    mutationFn: async (fd) => (await api.post("/documents/upload", fd)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["my-documents"] }); toast.success("Document uploaded securely"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/documents/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["my-documents"] }); toast.success("Document removed"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const uploadDoc = (docType, input) => {
    const file = input.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("doc_type", docType);
    fd.append("file", file);
    if (expiry[docType]) fd.append("expiry_date", expiry[docType]);
    uploadMut.mutate(fd);
    input.value = "";
  };
  const openDoc = async (id) => {
    try {
      const res = await api.get(`/documents/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Could not open document");
    }
  };

  if (isLoading || !emp)
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;

  const f = form || {
    dob: emp.dob || "", phone: emp.phone || "", location: emp.location || "",
    emergency_contact_name: emp.emergency_contact_name || "",
    emergency_contact_phone: emp.emergency_contact_phone || "",
    bank_account_no: emp.bank_account_no || "", bank_ifsc: emp.bank_ifsc || "",
  };
  const set = (k, v) => setForm({ ...f, [k]: v });

  const month = new Date().toISOString().slice(0, 7);
  const monthLogs = attendance.filter((l) => (l.date || "").startsWith(month));
  const present = monthLogs.filter((l) => l.checkin).length;
  const onLeave = monthLogs.filter((l) => l.status === "On Leave").length;
  const hoursArr = monthLogs.filter((l) => l.hours > 0).map((l) => l.hours);
  const avgHours = hoursArr.length ? (hoursArr.reduce((a, b) => a + b, 0) / hoursArr.length).toFixed(1) : "—";
  const recentSlips = [...payslips].sort((a, b) => b.month.localeCompare(a.month)).slice(0, 3);
  const pendingReq = myRequests.find((r) => r.status === "Pending");
  const t = tax || { regime: emp.tax_regime || "new", declared_80c: emp.declared_80c || 0 };

  return (
    <div>
      <PageHeader title="My Space" subtitle="Your personal employee self-service hub." />

      {/* Identity banner */}
      <Card className="p-6 border border-border rounded-xl mb-6 bg-gradient-to-br from-card to-secondary/40" data-testid="myspace-identity">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-accent text-accent-foreground flex items-center justify-center text-2xl font-extrabold">
            {emp.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-xl font-extrabold tracking-tight">{emp.name}</h2>
            <p className="text-sm text-muted-foreground">{emp.designation} • {emp.department}</p>
            <p className="text-xs font-mono text-muted-foreground mt-1">{emp.employee_id} • Joined {emp.doj} • {emp.location}</p>
          </div>
          <div className="text-right">
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Annual CTC</p>
            <p className="font-heading text-2xl font-extrabold text-accent">{fmtINR(emp.annual_ctc)}</p>
            <div className="mt-1"><StatusBadge status={emp.status} /></div>
          </div>
        </div>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: profile edit */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 border border-border rounded-xl" data-testid="profile-edit-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-accent" /> Personal & Bank Details</h3>
              {pendingReq && <span className="text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400 border border-amber-200 dark:border-amber-800" data-testid="pending-request-badge">Update pending HR approval</span>}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Date of Birth</Label><Input type="date" value={f.dob} onChange={(e) => set("dob", e.target.value)} data-testid="profile-dob-input" /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="profile-phone-input" /></div>
              <div className="space-y-1.5"><Label>Location / Address</Label><Input value={f.location} onChange={(e) => set("location", e.target.value)} data-testid="profile-location-input" /></div>
              <div className="space-y-1.5"><Label>Emergency Contact Name</Label><Input value={f.emergency_contact_name} onChange={(e) => set("emergency_contact_name", e.target.value)} data-testid="profile-emg-name-input" /></div>
              <div className="space-y-1.5"><Label>Emergency Contact Phone</Label><Input value={f.emergency_contact_phone} onChange={(e) => set("emergency_contact_phone", e.target.value)} data-testid="profile-emg-phone-input" /></div>
              <div className="space-y-1.5"><Label>Bank Account No.</Label><Input value={f.bank_account_no} onChange={(e) => set("bank_account_no", e.target.value)} data-testid="profile-bank-acc-input" /></div>
              <div className="space-y-1.5"><Label>Bank IFSC</Label><Input value={f.bank_ifsc} onChange={(e) => set("bank_ifsc", e.target.value)} data-testid="profile-bank-ifsc-input" /></div>
            </div>
            <div className="flex items-center gap-3 mt-4">
              <Button onClick={() => reqMut.mutate(f)} disabled={reqMut.isPending} data-testid="profile-submit-button" className="rounded-lg font-semibold active:scale-[0.98]">
                {reqMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Request Update</>}
              </Button>
              <p className="text-xs text-muted-foreground">Changes require HR approval before they take effect.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mt-5 pt-4 border-t border-border text-sm">
              <p className="flex items-center gap-2 text-muted-foreground"><Cake className="w-3.5 h-3.5" /> DOB: <span className="font-mono text-foreground">{emp.dob || "—"}</span></p>
              <p className="flex items-center gap-2 text-muted-foreground"><Building2 className="w-3.5 h-3.5" /> PAN: <span className="font-mono text-foreground">{emp.pan || "—"}</span></p>
              <p className="flex items-center gap-2 text-muted-foreground"><Landmark className="w-3.5 h-3.5" /> UAN: <span className="font-mono text-foreground">{emp.uan || "—"}</span></p>
            </div>
          </Card>

          {/* Tax regime */}
          <Card className="p-6 border border-border rounded-xl" data-testid="tax-regime-card">
            <h3 className="font-heading font-semibold mb-1 flex items-center gap-2"><Calculator className="w-4 h-4 text-accent" /> Income Tax Regime</h3>
            <p className="text-xs text-muted-foreground mb-4">Choose the regime for your TDS. Changes apply from the next payroll run and are shown on your payslip.</p>
            <div className="grid sm:grid-cols-3 gap-3 items-end">
              <div className="space-y-1.5"><Label>Regime</Label>
                <Select value={t.regime} onValueChange={(v) => setTax({ ...t, regime: v })}>
                  <SelectTrigger data-testid="tax-regime-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New Regime (default)</SelectItem>
                    <SelectItem value="old">Old Regime (with 80C)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>80C Declaration (₹, max 1,50,000)</Label>
                <Input type="number" min="0" max="150000" disabled={t.regime !== "old"} value={t.declared_80c} onChange={(e) => setTax({ ...t, declared_80c: Number(e.target.value) })} data-testid="tax-80c-input" />
              </div>
              <Button onClick={() => taxMut.mutate(t)} disabled={taxMut.isPending || !tax} className="rounded-lg font-semibold" data-testid="tax-save-button">
                {taxMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Declaration"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-3" data-testid="tax-summary">
              Current: <span className="font-semibold text-foreground">{(emp.tax_regime || "new") === "old" ? "Old Regime" : "New Regime"}</span> • Estimated TDS <span className="font-mono text-foreground">{fmtINR(emp.breakdown?.monthly?.tds || 0)}/month</span> • Net take-home <span className="font-mono text-foreground">{fmtINR(emp.breakdown?.net_monthly || 0)}/month</span>
            </p>
          </Card>

          {/* Document Vault */}
          <Card className="p-6 border border-border rounded-xl" data-testid="document-vault">
            <h3 className="font-heading font-semibold mb-1 flex items-center gap-2"><FolderLock className="w-4 h-4 text-accent" /> Document Vault</h3>
            <p className="text-xs text-muted-foreground mb-4">Securely upload your identity documents — PDF, JPG or PNG, up to 5MB. Only you and HR can view them.</p>
            <div className="space-y-3">
              {["PAN", "Aadhaar", "UAN"].map((t) => {
                const d = documents.find((x) => x.doc_type === t);
                return (
                  <div key={t} className="flex items-center gap-3 p-3 rounded-lg border border-border" data-testid={`doc-row-${t}`}>
                    <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center"><FileText className="w-4 h-4 text-muted-foreground" /></div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold flex items-center gap-2">{t} {d && <StatusBadge status={d.verification_status || "Pending"} />}</p>
                      <p className="text-xs text-muted-foreground truncate">{d ? d.original_filename : "Not uploaded"}</p>
                      {d?.expiry_date && <p className="text-[10px] text-amber-600 dark:text-amber-400">Expires {d.expiry_date}</p>}
                      {!d && <input type="date" value={expiry[t] || ""} onChange={(e) => setExpiry((x) => ({ ...x, [t]: e.target.value }))} className="mt-1 text-[11px] border border-border rounded px-1.5 py-0.5 bg-background" data-testid={`doc-expiry-${t}`} title="Optional expiry date" />}
                    </div>
                    {d ? (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => openDoc(d.id)} data-testid={`doc-view-${t}`}><Eye className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => delMut.mutate(d.id)} data-testid={`doc-delete-${t}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                        <label className="cursor-pointer text-xs text-accent font-medium px-2">
                          Replace
                          <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" data-testid={`doc-upload-${t}`} onChange={(e) => uploadDoc(t, e.target)} />
                        </label>
                      </>
                    ) : (
                      <label className="cursor-pointer">
                        <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-primary text-primary-foreground font-medium active:scale-[0.98] transition-transform"><Upload className="w-3.5 h-3.5" /> Upload</span>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" data-testid={`doc-upload-${t}`} onChange={(e) => uploadDoc(t, e.target)} />
                      </label>
                    )}
                  </div>
                );
              })}
            </div>
            {uploadMut.isPending && <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Uploading securely…</p>}
          </Card>

          {/* Recent payslips */}
          <Card className="border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2"><Wallet className="w-4 h-4 text-accent" /><h3 className="font-heading font-semibold">Recent Payslips</h3></div>
            <div className="divide-y divide-border">
              {recentSlips.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No payslips yet.</p>}
              {recentSlips.map((p) => (
                <div key={p.id} className="flex items-center gap-3 px-5 py-3.5" data-testid={`myspace-payslip-${p.id}`}>
                  <div className="w-10 h-10 rounded-lg bg-teal-50 text-teal-600 dark:bg-teal-950/50 dark:text-teal-400 flex items-center justify-center"><Wallet className="w-4 h-4" /></div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold">{new Date(p.month + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</p>
                    <p className="text-xs text-muted-foreground">Net {fmtINR(p.net)}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setViewSlip(p)} data-testid={`myspace-view-payslip-${p.id}`}><Eye className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
          </Card>

          {/* Change history */}
          <Card className="border border-border rounded-xl overflow-hidden" data-testid="profile-history">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2"><History className="w-4 h-4 text-accent" /><h3 className="font-heading font-semibold">Profile Change History</h3></div>
            <div className="divide-y divide-border">
              {auditLog.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No approved changes yet.</p>}
              {auditLog.map((a) => (
                <div key={a.id} className="px-5 py-3" data-testid={`history-row-${a.id}`}>
                  <p className="text-[11px] text-muted-foreground">{new Date(a.approved_at).toLocaleString("en-IN")} • approved by {a.approved_by}</p>
                  <p className="text-sm">{Object.entries(a.changes).map(([k, v]) => `${k}: ${v}`).join(" • ")}</p>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Right: leave balance + attendance summary */}
        <div className="space-y-6">
          <Card className="p-5 border border-border rounded-xl" data-testid="my-offer-card">
            <h3 className="font-heading font-semibold mb-2 flex items-center gap-2"><FileText className="w-4 h-4 text-accent" /> My Offer Letter</h3>
            {myOffer ? (
              <>
                <p className="text-xs text-muted-foreground mb-3">{myOffer.designation} • {fmtINR(myOffer.annual_ctc)} • Joining {myOffer.joining_date}</p>
                <Button size="sm" variant="outline" className="w-full rounded-lg" onClick={() => setViewOffer(true)} data-testid="view-my-offer-button"><Eye className="w-4 h-4 mr-1.5" /> View Offer Letter</Button>
              </>
            ) : (
              <p className="text-xs text-muted-foreground" data-testid="my-offer-empty">No offer letter is linked to your account.</p>
            )}
          </Card>

          <Card className="p-5 border border-border rounded-xl" data-testid="login-activity-card">
            <h3 className="font-heading font-semibold mb-2 flex items-center gap-2"><KeyRound className="w-4 h-4 text-accent" /> Recent Sign-ins</h3>
            <p className="text-[11px] text-muted-foreground mb-2">Don't recognise a device? Change your password right away.</p>
            <LoginActivity compact />
          </Card>

          <Card className="p-5 border border-border rounded-xl">
            <h3 className="font-heading font-semibold mb-4 flex items-center gap-2"><Palmtree className="w-4 h-4 text-accent" /> Leave Balance</h3>
            <div className="space-y-3">
              {["Casual", "Sick", "Earned"].map((t) => (
                <div key={t} className="flex items-center justify-between" data-testid={`myspace-balance-${t}`}>
                  <span className="text-sm text-muted-foreground">{t}</span>
                  <span className={`px-2.5 py-0.5 rounded-full text-sm font-bold font-mono ${BAL_TINTS[t]}`}>{balance[t] ?? "—"} days</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-5 border border-border rounded-xl">
            <h3 className="font-heading font-semibold mb-4 flex items-center gap-2"><Clock className="w-4 h-4 text-accent" /> This Month</h3>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div><p className="font-heading text-2xl font-extrabold">{present}</p><p className="text-[11px] text-muted-foreground">Present</p></div>
              <div><p className="font-heading text-2xl font-extrabold">{onLeave}</p><p className="text-[11px] text-muted-foreground">On Leave</p></div>
              <div><p className="font-heading text-2xl font-extrabold">{avgHours}</p><p className="text-[11px] text-muted-foreground">Avg Hrs</p></div>
            </div>
          </Card>

          <Card className="p-5 border border-border rounded-xl">
            <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><Phone className="w-4 h-4 text-accent" /> On Record</h3>
            <div className="space-y-1.5 text-sm text-muted-foreground">
              <p>Phone: <span className="text-foreground">{emp.phone || "—"}</span></p>
              <p>Emergency: <span className="text-foreground">{emp.emergency_contact_name || "—"} {emp.emergency_contact_phone ? `(${emp.emergency_contact_phone})` : ""}</span></p>
              <p>Bank A/C: <span className="text-foreground font-mono">{emp.bank_account_no || "—"}</span></p>
              <p>IFSC: <span className="text-foreground font-mono">{emp.bank_ifsc || "—"}</span></p>
            </div>
          </Card>
        </div>
      </div>

      <Dialog open={viewOffer} onOpenChange={(o) => !o && setViewOffer(false)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="no-print">
            <DialogTitle className="font-heading flex items-center justify-between">
              Offer Letter
              <Button size="sm" variant="outline" className="rounded-lg mr-4" onClick={() => downloadLetterPdf(`My_Offer_Letter.pdf`)} data-testid="myspace-print-offer">
                <Download className="w-4 h-4 mr-1.5" /> Download PDF
              </Button>
            </DialogTitle>
            <DialogDescription className="sr-only">Your offer letter.</DialogDescription>
          </DialogHeader>
          {viewOffer && myOffer && <ErrorBoundary><OfferLetter offer={myOffer} /></ErrorBoundary>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewSlip} onOpenChange={(o) => !o && setViewSlip(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="no-print">
            <DialogTitle className="font-heading flex items-center justify-between">
              Payslip
              <Button size="sm" variant="outline" className="rounded-lg mr-4" onClick={() => downloadLetterPdf(`Payslip_${viewSlip?.month || ""}.pdf`)} data-testid="myspace-print-payslip">
                <Download className="w-4 h-4 mr-1.5" /> Download PDF
              </Button>
            </DialogTitle>
            <DialogDescription className="sr-only">Your monthly payslip.</DialogDescription>
          </DialogHeader>
          {viewSlip && <Payslip slip={viewSlip} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
